import { useState, useRef, useCallback, useEffect, type ReactNode, type ReactElement } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import { Popover, PopoverAnchor } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { useAsyncSearch } from '@/hooks/use-async-search';
import { cn } from '@/lib/utils';

interface AsyncComboboxProps<T> {
  value: string | null;
  onValueChange: (id: string | null, label: string) => void;
  searchFn: (query: string, signal: AbortSignal) => Promise<T[]>;
  getItemId: (item: T) => string;
  getItemLabel: (item: T) => string;
  renderItem: (item: T, isSelected: boolean) => ReactNode;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  id?: string;
  emptyMessage?: string;
  allowFreeText?: boolean;
  popoverClassName?: string;
  preventInitialOpen?: boolean;
  initialLabel?: string;
  className?: string;
}

export function AsyncCombobox<T>({
  value,
  onValueChange,
  searchFn,
  getItemId,
  getItemLabel,
  renderItem,
  placeholder,
  hasError = false,
  disabled = false,
  id,
  emptyMessage = 'No results found.',
  allowFreeText = false,
  popoverClassName,
  preventInitialOpen = false,
  initialLabel,
  className,
}: AsyncComboboxProps<T>): ReactElement {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(initialLabel ?? '');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLLIElement>>(new Map());
  const isSelectingRef = useRef(false);
  const hasReceivedFocusRef = useRef(false);

  const wrappedSearchFn = useCallback(
    (query: string, signal: AbortSignal): Promise<T[]> => searchFn(query, signal),
    [searchFn]
  );

  const { results, isLoading, setSearch, prefetch } = useAsyncSearch<T>({
    searchFn: wrappedSearchFn,
  });

  const prevValueRef = useRef<string | null>(null);
  const labelResolvedRef = useRef(initialLabel != null && initialLabel !== '');

  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      labelResolvedRef.current = false;

      if (value === null) {
        if (!allowFreeText) {
          setInputValue('');
        }
        labelResolvedRef.current = true;
        return;
      }

      if (initialLabel != null && initialLabel !== '') {
        setInputValue(initialLabel);
        labelResolvedRef.current = true;
        return;
      }
    }

    if (value != null && !labelResolvedRef.current) {
      const item = results.find((r) => getItemId(r) === value);
      if (item) {
        setInputValue(getItemLabel(item));
        labelResolvedRef.current = true;
      } else if (!isLoading) {
        prefetch();
      }
    }
  }, [value, results, getItemId, getItemLabel, allowFreeText, isLoading, prefetch, initialLabel]);

  useEffect(() => {
    if (initialLabel != null && initialLabel !== '' && !labelResolvedRef.current && value != null) {
      setInputValue(initialLabel);
      labelResolvedRef.current = true;
    }
  }, [initialLabel, value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearch(newValue);
    setHighlightedIndex(-1);

    if (!open) {
      setOpen(true);
    }

    if (allowFreeText) {
      const match = results.find(
        (item) => getItemLabel(item).toLowerCase() === newValue.toLowerCase()
      );
      if (match) {
        onValueChange(getItemId(match), getItemLabel(match));
      } else {
        onValueChange(null, newValue);
      }
    }
  };

  const selectItem = useCallback(
    (item: T): void => {
      isSelectingRef.current = true;
      const itemId = getItemId(item);
      const itemLabel = getItemLabel(item);
      setInputValue(itemLabel);
      onValueChange(itemId, itemLabel);
      setOpen(false);
      setHighlightedIndex(-1);
      requestAnimationFrame(() => {
        isSelectingRef.current = false;
      });
    },
    [getItemId, getItemLabel, onValueChange]
  );

  const handleInputFocus = (): void => {
    if (preventInitialOpen && !hasReceivedFocusRef.current) {
      hasReceivedFocusRef.current = true;
      return;
    }
    if (!open) {
      setOpen(true);
      if (results.length === 0) {
        prefetch();
      }
      if (value != null) {
        requestAnimationFrame(() => {
          const selectedIndex = results.findIndex((r) => getItemId(r) === value);
          if (selectedIndex >= 0) {
            scrollItemIntoView(selectedIndex);
          }
        });
      }
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    const relatedTarget = e.relatedTarget as Node | null;
    if (relatedTarget && containerRef.current?.contains(relatedTarget)) {
      return;
    }

    requestAnimationFrame(() => {
      if (isSelectingRef.current) return;
      setOpen(false);
      setHighlightedIndex(-1);

      if (!allowFreeText && value === null) {
        setInputValue('');
      } else if (!allowFreeText && value != null) {
        const item = results.find((r) => getItemId(r) === value);
        if (item) {
          setInputValue(getItemLabel(item));
        }
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
        prefetch();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const nextIndex = highlightedIndex < results.length - 1 ? highlightedIndex + 1 : 0;
        setHighlightedIndex(nextIndex);
        scrollItemIntoView(nextIndex);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prevIndex = highlightedIndex > 0 ? highlightedIndex - 1 : results.length - 1;
        setHighlightedIndex(prevIndex);
        scrollItemIntoView(prevIndex);
        break;
      }
      case 'Enter': {
        e.preventDefault();
        const selected = results[highlightedIndex];
        if (highlightedIndex >= 0 && selected != null) {
          selectItem(selected);
        }
        break;
      }
      case 'Escape': {
        e.preventDefault();
        setOpen(false);
        setHighlightedIndex(-1);
        break;
      }
    }
  };

  const scrollItemIntoView = (index: number): void => {
    const el = itemRefs.current.get(index);
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  };

  const handleItemMouseDown = (e: React.MouseEvent): void => {
    e.preventDefault();
  };

  const handleItemClick = (item: T): void => {
    selectItem(item);
    inputRef.current?.focus();
  };

  // Radix Dialog/AlertDialog locks page scroll via RemoveScroll, which also
  // swallows wheel events on anything portaled outside the dialog subtree (the
  // default body portal). Portal the dropdown into the enclosing dialog so the
  // list's own overflow scroll keeps working; fall back to the default body
  // portal when this combobox isn't rendered inside a dialog.
  const portalContainer =
    (containerRef.current?.closest('[role="dialog"],[role="alertdialog"]') as HTMLElement | null) ??
    undefined;

  return (
    <Popover open={open}>
      <PopoverAnchor asChild>
        <div ref={containerRef} className={cn('relative', className)}>
          <Input
            ref={inputRef}
            id={id}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={cn('h-8 text-sm pr-8', hasError && 'border-destructive')}
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            autoComplete="off"
          />
          {isLoading ? (
            <Loader2
              size={14}
              className="absolute right-8 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground pointer-events-none"
            />
          ) : null}
          <ChevronDown
            size={16}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/50 pointer-events-none"
          />
        </div>
      </PopoverAnchor>
      <PopoverPrimitive.Portal container={portalContainer}>
        <PopoverPrimitive.Content
          data-slot="popover-content"
          className={cn(
            'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 origin-(--radix-popover-content-transform-origin) rounded-md border shadow-md outline-hidden',
            'min-w-[--radix-popper-anchor-width] overflow-hidden p-0',
            popoverClassName
          )}
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            const target = e.target as Node | null;
            if (target && containerRef.current?.contains(target)) {
              e.preventDefault();
              return;
            }
            setOpen(false);
            setHighlightedIndex(-1);
          }}
        >
          <div className="max-h-80 overflow-y-auto">
            {results.length > 0 ? (
              <ul ref={listRef} role="listbox" className="py-1">
                {results.map((item, index) => {
                  const itemId = getItemId(item);
                  const isSelected = itemId === value;
                  const isHighlighted = index === highlightedIndex;
                  return (
                    <li
                      key={itemId}
                      ref={(el) => {
                        if (el) {
                          itemRefs.current.set(index, el);
                        } else {
                          itemRefs.current.delete(index);
                        }
                      }}
                      role="option"
                      aria-selected={isSelected}
                      onMouseDown={handleItemMouseDown}
                      onClick={() => handleItemClick(item)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={cn(
                        'cursor-pointer px-3 py-1.5 text-sm outline-none transition-colors',
                        isHighlighted && 'bg-accent text-accent-foreground',
                        isSelected && !isHighlighted && 'bg-accent/50'
                      )}
                    >
                      {renderItem(item, isSelected)}
                    </li>
                  );
                })}
              </ul>
            ) : !isLoading ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </div>
            ) : null}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </Popover>
  );
}
